import { useCallback, useMemo } from 'react';
import {
  useInfiniteQuery,
  type DefaultError,
  type InfiniteData,
  type QueryKey,
  type UseInfiniteQueryOptions,
} from '@tanstack/react-query';
import type { PaginationMeta } from '@/api/pagination';

interface PagedRows<TRow, TMeta extends PaginationMeta> {
  data: TRow[];
  meta: TMeta;
}

export function useInfiniteTableQuery<
  TRow,
  TMeta extends PaginationMeta,
  TQueryKey extends QueryKey,
>(
  options: UseInfiniteQueryOptions<
    PagedRows<TRow, TMeta>,
    DefaultError,
    InfiniteData<PagedRows<TRow, TMeta>>,
    TQueryKey,
    number
  >,
) {
  const {
    data,
    error,
    isLoading,
    isRefetching,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useInfiniteQuery(options);

  const rows = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data]);

  const handleLoadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  return {
    rows,
    firstPageMeta: data?.pages[0]?.meta,
    loadedPageCount: data?.pages.length ?? 0,
    isLoading,
    isRefetching,
    loadError: isFetchNextPageError ? null : error,
    refetch,
    loadMoreProps: {
      onLoadMore: handleLoadMore,
      hasMore: hasNextPage,
      isLoadingMore: isFetchingNextPage,
      loadMoreError: isFetchNextPageError,
    },
  };
}
